import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  EnvelopeSimple, CheckCircle, Eye, CursorClick,
  Warning, ArrowClockwise, XCircle,
} from "@phosphor-icons/react";

const RANGES = [
  { key: 7, label: "7 days" },
  { key: 14, label: "14 days" },
  { key: 30, label: "30 days" },
  { key: 60, label: "60 days" },
  { key: 90, label: "90 days" },
];

const EVENT_STYLES = {
  delivered:         { color: "text-emerald-700", bg: "bg-emerald-50", label: "Delivered" },
  opened:            { color: "text-blue-700",    bg: "bg-blue-50",    label: "Opened" },
  clicked:           { color: "text-violet-700",  bg: "bg-violet-50",  label: "Clicked" },
  sent:              { color: "text-rf-ink-soft", bg: "bg-rf-cream-alt", label: "Sent" },
  delivery_delayed:  { color: "text-amber-700",   bg: "bg-amber-50",   label: "Delayed" },
  bounced:           { color: "text-red-700",     bg: "bg-red-50",     label: "Bounced" },
  complained:        { color: "text-orange-700",  bg: "bg-orange-50",  label: "Complained" },
  failed:            { color: "text-red-700",     bg: "bg-red-50",     label: "Failed" },
};

function StatCard({ icon: Icon, label, value, sub, tone = "ink", testid }) {
  const tones = {
    ink: "text-rf-ink",
    green: "text-emerald-600",
    blue: "text-blue-600",
    violet: "text-violet-600",
    red: "text-red-600",
  };
  return (
    <div className="card-recipe" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={20} weight="duotone" className="text-rf-orange" />
        <p className="text-xs font-bold uppercase tracking-wider text-rf-ink-soft">{label}</p>
      </div>
      <p className={`font-display font-black text-3xl ${tones[tone]}`}>{value}</p>
      {sub && <p className="text-xs text-rf-ink-soft mt-1">{sub}</p>}
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function EmailAnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/admin/email-analytics?days=${d}`);
      setData(res);
      if (res.error) toast.error("Analytics unavailable", { description: res.error });
    } catch (e) {
      toast.error("Could not load analytics", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(30); }, []); // initial load only; range changes trigger their own load() call

  const totals = data?.totals || {};
  const rates = data?.rates || {};
  const sentTotal = Object.values(totals).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="admin-email-analytics">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="overline">Email analytics</p>
          <h2 className="font-display font-black text-2xl text-rf-ink">Resend transactional stats</h2>
          <p className="text-rf-ink-soft text-sm mt-1">
            Live from the Resend API — no webhook setup required. Refreshes on demand.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 bg-white border border-rf-ink/10 rounded-full p-1" data-testid="email-analytics-range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => { setDays(r.key); load(r.key); }}
                data-testid={`email-analytics-range-${r.key}`}
                className={`px-3 py-1 rounded-full text-xs font-bold transition ${days === r.key ? "bg-rf-ink text-rf-cream" : "text-rf-ink-soft hover:bg-rf-cream-alt"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => load(days)}
            disabled={loading}
            data-testid="email-analytics-refresh"
            className="btn-outline-ink text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <ArrowClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
            {loading ? "Loading" : "Refresh"}
          </button>
        </div>
      </div>

      {!data && loading && (
        <p className="text-rf-ink-soft text-center py-14" data-testid="email-analytics-loading">Fetching from Resend…</p>
      )}

      {data && data.error && !loading && (
        <div className="card-recipe border-l-4 border-red-400" data-testid="email-analytics-error">
          <p className="font-bold text-red-700 mb-1">Analytics unavailable</p>
          <p className="text-sm text-rf-ink-soft">{data.error}</p>
        </div>
      )}

      {data && !data.error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard icon={EnvelopeSimple} label="Sent"     value={sentTotal}         sub={`in the last ${days} days`} testid="stat-sent" />
            <StatCard icon={CheckCircle}    label="Delivery" value={`${rates.delivery ?? 0}%`} sub={`${totals.delivered + totals.opened + totals.clicked || 0} delivered`} tone="green"  testid="stat-delivery" />
            <StatCard icon={Eye}            label="Open"     value={`${rates.open ?? 0}%`}     sub={`${totals.opened + totals.clicked || 0} opened`}                     tone="blue"   testid="stat-open" />
            <StatCard icon={CursorClick}    label="Click"    value={`${rates.click ?? 0}%`}    sub={`${totals.clicked || 0} clicked`}                                   tone="violet" testid="stat-click" />
            <StatCard icon={XCircle}        label="Bounce"   value={`${rates.bounce ?? 0}%`}   sub={`${(totals.bounced || 0) + (totals.failed || 0)} bounced/failed`}   tone="red"    testid="stat-bounce" />
          </div>

          {(totals.complained > 0 || totals.delivery_delayed > 0) && (
            <div className="flex flex-wrap gap-2 text-sm">
              {totals.complained > 0 && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 font-bold">
                  <Warning size={14} weight="duotone" /> {totals.complained} spam complaint{totals.complained === 1 ? "" : "s"}
                </span>
              )}
              {totals.delivery_delayed > 0 && (
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 font-bold">
                  <Warning size={14} weight="duotone" /> {totals.delivery_delayed} delayed
                </span>
              )}
            </div>
          )}

          <div className="card-recipe">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="font-display font-black text-lg text-rf-ink">Recent emails</p>
              <p className="text-xs text-rf-ink-soft">
                Sampled {data.fetched} email{data.fetched === 1 ? "" : "s"}
                {data.sampled && " (capped at 2 000 for performance)"} · as of {fmtTime(data.as_of)}
              </p>
            </div>
            {data.recent.length === 0 ? (
              <p className="text-rf-ink-soft text-sm py-8 text-center" data-testid="email-analytics-empty">
                No emails sent in this window.
              </p>
            ) : (
              <div className="overflow-x-auto -mx-6 md:mx-0">
                <table className="w-full text-sm" data-testid="email-analytics-table">
                  <thead>
                    <tr className="text-left text-xs uppercase text-rf-ink-soft border-b border-rf-ink/10">
                      <th className="py-2 px-3 font-bold">Sent</th>
                      <th className="py-2 px-3 font-bold">To</th>
                      <th className="py-2 px-3 font-bold">Subject</th>
                      <th className="py-2 px-3 font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((e) => {
                      const s = EVENT_STYLES[e.last_event] || EVENT_STYLES.sent;
                      return (
                        <tr key={e.id} className="border-b border-rf-ink/5" data-testid={`email-row-${e.id}`}>
                          <td className="py-2 px-3 whitespace-nowrap text-xs text-rf-ink-soft">{fmtTime(e.created_at)}</td>
                          <td className="py-2 px-3 max-w-xs truncate">{e.to}</td>
                          <td className="py-2 px-3 max-w-md truncate">{e.subject || <em className="text-rf-ink-soft">(no subject)</em>}</td>
                          <td className="py-2 px-3">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${s.bg} ${s.color}`}>
                              {s.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
